/**
 * Gallery System Types
 *
 * Types for the user gallery feature including layouts, filters,
 * pagination, saved configurations, and A/B testing.
 */

// ==========================================
// LAYOUT TYPES
// ==========================================

export type GalleryLayoutVariant = 'grid' | 'row' | 'list';

export interface LayoutConfig {
  variant: GalleryLayoutVariant;
  label: string;
  description: string;
  icon: string;
}

export const LAYOUT_CONFIGS: LayoutConfig[] = [
  { variant: 'grid', label: 'Grid', description: 'Multi-row grid view', icon: '▦' },
  { variant: 'row', label: 'Row', description: 'Single horizontal row', icon: '▬' },
  { variant: 'list', label: 'List', description: 'Vertical list view', icon: '☰' },
];

// ==========================================
// GALLERY PROFILE CARD
// ==========================================

export interface GalleryProfileCard {
  profileId: string;
  displayName: string;
  age: number;
  gender?: string;
  primaryPhotoUrl: string;
  thumbnailUrl?: string;
  isVerified: boolean;
  isOnline?: boolean;
  distanceKm?: number;
  locationCity?: string;
  bio?: string;
  interests?: string[];
  characterClass?: string;
  lastActiveAt?: string;
}

// ==========================================
// FILTER TYPES
// ==========================================

export interface GalleryFilter {
  // Demographics
  minAge?: number;
  maxAge?: number;
  genders?: string[];
  characterClasses?: string[];

  // Location
  locationCity?: string;
  maxDistanceKm?: number;

  // Lifestyle
  smoking?: string;
  drinking?: string;
  exercise?: string;
  isGamer?: boolean;

  // Intent
  lookingFor?: string[];
  gamingFrequency?: string;
  gamingPlatforms?: string[];

  // Interests
  interests?: string[];

  // Verification & Activity
  isVerified?: boolean;
  activeWithinDays?: number;

  // Sorting
  sortBy?: 'recommended' | 'distance' | 'active' | 'newest' | 'age';
  sortOrder?: 'asc' | 'desc';
}

export const DEFAULT_GALLERY_FILTER: GalleryFilter = {
  minAge: 18,
  maxAge: 99,
  maxDistanceKm: 100,
  sortBy: 'recommended',
  sortOrder: 'desc',
};

// ==========================================
// FILTER OPTIONS
// ==========================================

export const SORT_OPTIONS: { value: GalleryFilter['sortBy']; label: string }[] = [
  { value: 'recommended', label: 'Recommended' },
  { value: 'distance', label: 'Nearest' },
  { value: 'active', label: 'Recently Active' },
  { value: 'newest', label: 'Newest Members' },
  { value: 'age', label: 'Age' },
];

export const ACTIVE_WITHIN_OPTIONS: { value: number; label: string }[] = [
  { value: 1, label: 'Today' },
  { value: 7, label: 'This week' },
  { value: 30, label: 'This month' },
  { value: 90, label: 'Last 3 months' },
];

// ==========================================
// SAVED GALLERY CONFIGURATION
// ==========================================

export interface SavedGalleryConfig {
  id: string;
  name: string;
  description?: string;
  filters: GalleryFilter;
  layout: GalleryLayoutVariant;
  createdAt: string;
  updatedAt: string;
}

export interface CreateSavedGalleryRequest {
  name: string;
  description?: string;
  filters: GalleryFilter;
  layout: GalleryLayoutVariant;
}

export interface UpdateSavedGalleryRequest {
  name?: string;
  description?: string;
  filters?: GalleryFilter;
  layout?: GalleryLayoutVariant;
}

// ==========================================
// PAGINATION
// ==========================================

export interface GalleryPaginationState {
  currentPage: number;
  pageSize: number;
  totalPages: number;
  totalCount: number;
  hasNextPage: boolean;
  hasPreviousPage: boolean;
}

export const DEFAULT_PAGE_SIZE = 20;

export const PAGE_SIZE_OPTIONS = [12, 20, 40, 60];

// ==========================================
// API RESPONSE
// ==========================================

export interface GalleryResponse {
  profiles: GalleryProfileCard[];
  pagination: GalleryPaginationState;
}

// ==========================================
// A/B TEST CONFIGURATION
// ==========================================

export const AB_TEST_ID = 'gallery-layout-v1';

export interface ABTestConfig {
  testId: string;
  variant: GalleryLayoutVariant;
  assignedAt: string;
  userId?: string;
}

// ==========================================
// GALLERY CARD VARIANTS
// ==========================================

export type GalleryCardVariant = 'icon' | 'compact' | 'full';

export interface GalleryCardConfig {
  variant: GalleryCardVariant;
  size: number;
  showName: boolean;
  showAge: boolean;
  showLocation: boolean;
  showVerified: boolean;
  showOnline: boolean;
}

export const CARD_CONFIGS: Record<GalleryCardVariant, GalleryCardConfig> = {
  icon: {
    variant: 'icon',
    size: 64,
    showName: true,
    showAge: false,
    showLocation: false,
    showVerified: true,
    showOnline: true,
  },
  compact: {
    variant: 'compact',
    size: 120,
    showName: true,
    showAge: true,
    showLocation: false,
    showVerified: true,
    showOnline: true,
  },
  full: {
    variant: 'full',
    size: 200,
    showName: true,
    showAge: true,
    showLocation: true,
    showVerified: true,
    showOnline: true,
  },
};

// ==========================================
// ANALYTICS
// ==========================================

export interface GalleryAnalyticsEvent {
  eventType: 'layout_view' | 'profile_click' | 'filter_change' | 'page_change';
  variant: GalleryLayoutVariant;
  timestamp: string;
  userId?: string;
  profileId?: string;
  position?: number;
  filters?: GalleryFilter;
  page?: number;
}
